import './styles/ClientRetrievalPieceStyle.css';
import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  LockerColumn,
  NavigationButton,
  ToastType,
  TopNav,
  TopNavToastMessage,
} from '../common';
import { languageMap } from '../../maps/languageMap';
import { useActions, useTypedSelector } from '../../hooks';
import { APIResponseStatusType, navigationPages } from '../../models/types';
import { useHistory } from 'react-router-dom';
import {
  ExitConfirmationRequestPOST,
  ExitConfirmationResponsePOST,
} from '../../models/cruds';
import { apiErrorHandler, apiPOST } from '../../restApi';
import {
  LockerColumnStatuses,
  LockerColumnTypes,
  LockerPosition,
  LockerPositionStatus,
  LockerPositionType,
} from '../../models/microservices';
import { Lock } from '../../devices/locker/interface/Locker/Lock';

const API_VALIDATION_ERROR = 'Error en la consulta';
const PRIMARY_COLUMN_COMPARTMENT_LENGTH = 8;
const PRIMARY_MASTER_COMPARTMENT_INDEX = 1;
const PRIMARY_MASTER_COMPARTMENT = {
  lockerPositionType: {
    name: LockerColumnTypes.SCREEN,
  } as LockerPositionType,
  lockerPositionStatus: {
    name: LockerColumnStatuses.BLOCKED,
  } as LockerPositionStatus,
} as LockerPosition;

const LOCKER_MESSAGE_OPENING = '¡Abriendo tu locker!';
const LOCKER_MESSAGE_OPENED = ' Puedes retirar tu envío';
const LOCKER_MESSAGE_RETRIEVED = 'Envío retirado, por favor cierra la puerta.';

const ClientRetrievalPiece: React.FC = () => {
  let history = useHistory();
  const retrieveLockRef = useRef<Lock>();
  const [loading, setLoading] = useState(false);
  const [lockOpened, setLockOpened] = useState(false);
  const [canContinue, setCanContinue] = useState(false);
  const [retrieveLock, setRetrieveLock] = useState<Lock>();
  const [confirmationMessage, setConfirmationMessage] = useState(
    LOCKER_MESSAGE_OPENING
  );
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const language = useTypedSelector(({ session }) => {
    return session.language;
  });
  const token = useTypedSelector(({ session }) => {
    return session.dhlApiToken;
  });
  const customerName = useTypedSelector(({ locker }) => {
    return locker.customerName;
  });
  const customerSignature = useTypedSelector(({ locker }) => {
    return locker.customerName;
  });
  const currentLockerPosition = useTypedSelector(({ locker }) => {
    return locker.currentLockerPosition;
  });
  const lockerContent = useTypedSelector(({ locker }) => {
    return locker.lockerContent;
  });
  const locker = useTypedSelector(({ devices }) => {
    return devices.locker;
  });
  const { updateIsMultiDelivery, updateCurrentLockerPosition } = useActions();
  retrieveLockRef.current = retrieveLock;
  //On Loading
  useEffect(() => {
    console.log('on loading');
    addLockerEventListeners();
  }, []);
  //On unloading
  useEffect(
    () => () => {
      console.log('on unloading');
      removeLockerEventListeners();
    },
    []
  );
  const addLockerEventListeners = () => {
    locker?.addEventListener('lockOpened', onLockOpened as EventListener);
    locker?.addEventListener('lockClosed', onLockClosed as EventListener);
  };
  const removeLockerEventListeners = () => {
    locker?.removeEventListener('lockOpened', onLockOpened as EventListener);
    locker?.removeEventListener('lockClosed', onLockClosed as EventListener);
  };
  const onLockOpened = useCallback(
    (event: CustomEvent) => {
      const openedLock = event.detail as Lock;
      console.log('lock Opened', event.detail, retrieveLock, openedLock);
      if (
        openedLock.sid === retrieveLockRef.current?.sid &&
        openedLock.cid === retrieveLockRef.current?.cid
      ) {
        console.log('lock Opened true', event.detail);
        setConfirmationMessage(LOCKER_MESSAGE_OPENED);
        setLockOpened(true);
      }
    },
    [retrieveLock]
  );
  const onLockClosed = useCallback(
    (event: CustomEvent) => {
      const closedLock = event.detail as Lock;
      console.log('lock Closed ', retrieveLockRef, closedLock);
      if (
        closedLock.sid === retrieveLockRef.current?.sid &&
        closedLock.cid === retrieveLockRef.current?.cid
      ) {
        console.log('lock Closed true', event.detail);
        setLockOpened(false);
      }
    },
    [retrieveLock]
  );
  //#endregion
  useEffect(() => {
    const getAPISignature = () => {
      const SIGNATURE_METADATA = 'data:image/png;base64,';
      let base64Signature = customerSignature.replace(SIGNATURE_METADATA, '');
      return base64Signature;
    };
    const exitConfirmation = async () => {
      let packageCandidateId = currentLockerPosition.packageCandidate?.id ?? 0;
      let lockerPositionId = currentLockerPosition.id;
      let signature = getAPISignature();
      let request = new ExitConfirmationRequestPOST();
      let response: ExitConfirmationResponsePOST = await apiPOST(
        request.buildURL(),
        request.generateContent(
          packageCandidateId,
          lockerPositionId,
          customerName,
          signature
        ),
        request.generateConfig(token)
      );
      let errorMsg = '';
      try {
        if (response.status === APIResponseStatusType.SUCCESS) {
          if (response.data.exitConfirmation) {
            setCanContinue(true);
            updateCurrentLockerPosition({
              ...currentLockerPosition,
              retrieved: true,
            });
            setConfirmationMessage(LOCKER_MESSAGE_RETRIEVED);
            setLoading(false);
            return;
          } else {
            //TODO check for unauth and refresh token
            errorMsg = 'Unauthorized';
          }
        } else {
          errorMsg = apiErrorHandler(response);
        }
      } catch (err: any) {
        errorMsg = err.toString();
      }
      console.log(errorMsg);
      setFeedbackMessage(API_VALIDATION_ERROR);
      setLoading(false);
      return false;
    };
    if (
      lockOpened &&
      currentLockerPosition.packageCandidate &&
      !currentLockerPosition.retrieved
    ) {
      setLoading(true);
      setConfirmationMessage(LOCKER_MESSAGE_OPENED);
      exitConfirmation();
    }
  }, [
    lockOpened,
    currentLockerPosition,
    history,
    token,
    customerName,
    customerSignature,
    updateCurrentLockerPosition,
  ]);
  useEffect(() => {
    if (lockOpened && !currentLockerPosition.retrieved) {
      console.log('Closing lock');
    }
  }, [lockOpened, currentLockerPosition, updateCurrentLockerPosition]);
  useEffect(() => {
    if (currentLockerPosition.retrieved) {
      setConfirmationMessage(LOCKER_MESSAGE_RETRIEVED);
      setCanContinue(true);
      setFeedbackMessage('');
    } else {
      setConfirmationMessage(LOCKER_MESSAGE_OPENING);
      setRetrieveLock(
        new Lock(
          currentLockerPosition.extraFields.sid,
          currentLockerPosition.extraFields.cid
        )
      );
    }
  }, [currentLockerPosition]);
  useEffect(() => {
    if (locker && retrieveLock?.sid && retrieveLock?.cid) {
      locker.openLockAsync(retrieveLock);
    }
  }, [locker, retrieveLock]);
  const renderLockerColumn = () => {
    let currentColumn = lockerContent
      .filter(
        (positions) =>
          positions.extraFields.sid === currentLockerPosition.extraFields.sid
      )
      .sort((a, b) => a.row.localeCompare(b.row));
    if (currentColumn.length === PRIMARY_COLUMN_COMPARTMENT_LENGTH) {
      currentColumn.splice(
        PRIMARY_MASTER_COMPARTMENT_INDEX,
        0,
        PRIMARY_MASTER_COMPARTMENT
      );
    }
    return (
      <LockerColumn
        onSelectCompartment={(position) => {}}
        lockerColumn={currentColumn}
        currentCompartment={currentLockerPosition}
      />
    );
  };
  const renderContent = () => {
    return (
      <div className="client-piece-container">
        <div className="client-piece-tag-container">
          <p className="client-piece-tag-instructions">{confirmationMessage}</p>
        </div>
        <div className="client-piece-locker-container">
          {renderLockerColumn()}
        </div>
      </div>
    );
  };
  const finishButton = () => {
    if (canContinue && !loading) {
      return (
        <NavigationButton
          buttonContent={
            languageMap.client_retrieval_piece.confirm_button[language]
          }
          buttonClass="navigation-button confirm-button complete"
          onClickButton={() => {
            history.replace(`/${navigationPages.THANK_YOU}`);
          }}
        />
      );
    }
  };
  const multiPieceButton = () => {
    if (canContinue && !loading) {
      return (
        <NavigationButton
          buttonContent={
            languageMap.client_retrieval_piece.other_piece_button[language]
          }
          buttonClass="navigation-button confirm-button complete"
          onClickButton={() => {
            updateIsMultiDelivery(true);
            updateCurrentLockerPosition({} as LockerPosition);
            history.replace(`/${navigationPages.CLIENT_RETRIEVAL_SCAN_AWB}`);
          }}
        />
      );
    }
  };
  return (
    <div id="clientRetrievalPiece" className="main-container">
      <TopNav dataCyPrefix="scan-awb" />
      <div className="content-container">
        <div className="main-content-container fullpage">
          <TopNavToastMessage
            toastText={feedbackMessage}
            toastType={ToastType.ERROR}
            resetMessage={() => setFeedbackMessage('')}
          />
          <div className="title-content-container vertical">
            <p className="header nopadding nomargin">
              {languageMap.client_retrieval_piece.title[language]}{' '}
              {customerName}!
            </p>
            <p className="subtitle nopadding">
              {languageMap.client_retrieval_name.subtitle[language]}{' '}
              {currentLockerPosition.packageCandidate?.awbNumber}
            </p>
          </div>
          <div className="data-content-container">{renderContent()}</div>
          <div className="footer-container">
            <div className="footer-button-container">
              <NavigationButton
                buttonContent={
                  languageMap.client_retrieval_piece.issue_button[language]
                }
                buttonClass="navigation-button cancel-button complete"
                onClickButton={() => {
                  history.replace(
                    `/${navigationPages.CLIENT_RETRIEVAL_ISSUES}`
                  );
                }}
              />
            </div>
            <div className="footer-button-container">{multiPieceButton()}</div>
            <div className="footer-button-container">{finishButton()}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export { ClientRetrievalPiece };
